import { CircularProgress, Grid } from '@mui/material';
import { toDayNoon } from '@nx-smartmonkey/shared/helpers';
import {
  HSpacer,
  RoutalDialog,
  RoutalDialogProps,
  RoutalInputDatePicker,
  RoutalPalette,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
} from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import AlertIcon from '../../../../assets/img/alert-icon.svg';
import { CheckmarkIcon } from '../../../../assets/svg/CheckmarkIcon';
import DialogInfo from '../../../../assets/svg/DialogInfo';
import { getPlanStatusColor } from '../../../../constants/Colors';
import { PlanStatus } from '../../../../domain/plan/PlanStatus';
import { NAME_SUBSTITUTION_CHARACTER_START, ProjectPlans } from '../../../../domain/project/ProjectPlans';
import { Vehicle } from '../../../../domain/vehicle/Vehicle';
import useCreatePlan from '../../../../hooks/plan/useCreatePlan';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActiveProjectResources } from '../../../../hooks/project/useRetrieveActiveProjectResources';
import { useRetrieveUser } from '../../../../hooks/user/useRetrieveUser';
import { useSearchVehiclesEnabled } from '../../../../hooks/vehicle/useSearchVehiclesEnabled';
import Button from '../../../../react-components/Button';
import InputText from '../../../../react-components/InputText';
import Text from '../../../../react-components/Text';
import eventsPanel from '../../../../services/events';
import { VehiclesSelection } from './VehiclesSelection';

const VEHICLE_LIMIT = 100;

interface CreatePlanModalProps extends RoutalDialogProps {
  selectedDay?: Date;
  handleAfterCreatePlan: (newPlanId: string, executionDate: Date) => void;
  setIsCreating?: (isCreating: boolean) => void;
}

export interface CreatePlanAllVehiclesObject {
  vehicle: Vehicle;
  selected: boolean;
}

export const CreatePlanModal = ({
  selectedDay = new Date(),
  handleAfterCreatePlan,
  setIsCreating,
  ...dialogProps
}: CreatePlanModalProps) => {
  const { data: user } = useRetrieveUser();
  const { data: projectResources } = useRetrieveActiveProjectResources();
  const { data: activeProject } = useRetrieveActiveProject();
  const [executionDate, setExecutionDate] = useState<Date>(toDayNoon(selectedDay));
  const [allVehicles, setAllVehicles] = useState<CreatePlanAllVehiclesObject[]>([]);
  const [label, setLabel] = useState<string>(``);
  const [updateStatus, setUpdateStatus] = useState<PlanStatus>(activeProject?.plans.status || `draft`);
  const [filterText, setFilterText] = useState<string | undefined>(undefined);
  const [vehicleOffset, setVehicleOffset] = useState<number>(0);
  const {
    run: createPlan,
    data: createdPlanId,
    success: planCreated,
    isLoading: isCreating,
  } = useCreatePlan({ project: activeProject });

  const { isLoading: searchingVehicles, data: paginatedVehicles } = useSearchVehiclesEnabled({
    offset: vehicleOffset,
    limit: VEHICLE_LIMIT,
    searchText: filterText,
  });

  useEffect(() => {
    // This will trigger just once, the first time we retrieve the paginated vehicles.
    // We do this to keep track of all the vehicles even after filtering.
    if ((allVehicles?.length ?? 0) < (paginatedVehicles?.total ?? 0)) {
      const allVehiclesRetrieved = (paginatedVehicles?.docs ?? []).map((vehicle) => ({
        vehicle,
        selected: (vehicle.default_working_days ?? []).includes(Vehicle.getWeekdayByDate(executionDate)),
      }));
      setAllVehicles(allVehiclesRetrieved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginatedVehicles]);

  const resultingQuota = () =>
    (projectResources?.max_vehicles_plan ?? 0) - allVehicles.filter((vehicleObject) => vehicleObject.selected).length;
  useEffect(() => {
    if (activeProject) {
      setLabel(
        ProjectPlans.labelTemplateToValue({
          template: activeProject.plans.label,
          date: selectedDay,
          language: user?.language.value,
        })
      );
      updateDate(activeProject.plans.defaultStartDateToValue(selectedDay));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay]);

  useEffect(() => {
    if (activeProject && activeProject.plans.label.includes(NAME_SUBSTITUTION_CHARACTER_START)) {
      setLabel(
        ProjectPlans.labelTemplateToValue({
          template: activeProject.plans.label,
          date: executionDate,
          language: user?.language.value,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionDate]);

  useEffect(() => {
    updateSelectedVehicles(executionDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionDate]);

  useEffect(() => {
    if (planCreated && createdPlanId) {
      handleAfterCreatePlan(createdPlanId, executionDate);
      setIsCreating && setIsCreating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdPlanId, handleAfterCreatePlan, planCreated]);

  if (!activeProject) return null;

  const handleCreatePlan = () => {
    if (setIsCreating) setIsCreating(true);
    const newDrivers = allVehicles
      .filter((vehicleObject) => vehicleObject.selected)
      .map((vehicleObject) => vehicleObject.vehicle.driverFromVehicle());
    eventsPanel.createDrivers(`Desde Nuevo Plan`);
    createPlan({
      label: label === `` ? undefined : label,
      drivers: newDrivers,
      status: updateStatus,
      executionDate: executionDate ?? activeProject.plans.defaultStartDateToValue(selectedDay),
    });
  };

  const updateSelectedVehicles = (date = executionDate) => {
    setAllVehicles(
      allVehicles.map((vehiclesObject) => ({
        ...vehiclesObject,
        selected: (vehiclesObject.vehicle.default_working_days ?? []).includes(Vehicle.getWeekdayByDate(date)),
      }))
    );
  };

  const updateDate = (date: Date) => {
    const dayNoonDate = toDayNoon(date);
    setExecutionDate(dayNoonDate);
    updateSelectedVehicles(dayNoonDate);
  };

  const renderFooterMessage = () => {
    if (resultingQuota() < 0) {
      return (
        <Grid container direction="row" alignItems="center" justifyContent="center" wrap="nowrap" gap="4px">
          <img src={AlertIcon} alt="" draggable="false" style={{ width: `14px` }} />
          <Text
            variant="body"
            intlId={`planner.create_plan.vehicles_limit_exceeded`}
            style={{ color: RoutalPalette.error.medium }}
          />
        </Grid>
      );
    }

    if (!allVehicles.filter((vehicleObject) => vehicleObject.selected).length && !searchingVehicles) {
      return (
        <Grid container direction="row" alignItems="center" justifyContent="center" wrap="nowrap" gap="8px">
          <DialogInfo style={{ color: RoutalPalette.primary40 }} />
          <Text intlId="create_plan_dialog.warning_no_vehicles" />
        </Grid>
      );
    }

    return null;
  };

  return (
    <RoutalDialog
      {...dialogProps}
      id="create-plan-dialog"
      contentStyles={{
        width: `100%`,
        padding: `0px`,
        overflow: `hidden`,
        height: `500px`,
      }}
      dialogProps={{
        maxWidth: `lg`,
        paperProps: {
          style: {
            maxWidth: `1000px`,
          },
        },
      }}
      dialogContentWarning={<div></div>}
      titleContent={
        <Grid
          container
          style={{
            display: `flex`,
            flexWrap: `nowrap`,
            alignItems: `center`,
            whiteSpace: `nowrap`,
          }}
        >
          <Text variant="h6" intlId="create_plan_dialog.title.v3" />
        </Grid>
      }
      footerContent={
        <Grid container alignItems="center" justifyContent="center" style={{ position: `relative`, height: `36px` }}>
          {renderFooterMessage()}

          <Button
            disabled={isCreating || resultingQuota() < 0}
            variant={
              !allVehicles.filter((vehicleObject) => vehicleObject.selected).length && !searchingVehicles
                ? `flat`
                : undefined
            }
            style={{
              color: RoutalPalette.primary40,
              margin: `0px`,
              position: `absolute`,
              right: 0,
            }}
            onClick={() => {
              if (!isCreating && resultingQuota() >= 0) handleCreatePlan();
            }}
          >
            {isCreating ? (
              <CircularProgress size={16} />
            ) : (
              <Text
                variant="button"
                intlId="create_plan_dialog.title.v3"
                style={{
                  color:
                    !allVehicles.filter((vehicleObject) => vehicleObject.selected).length && !searchingVehicles
                      ? undefined
                      : `white`,
                }}
              />
            )}
          </Button>
        </Grid>
      }
    >
      <Grid container direction="row" wrap="nowrap" style={{ height: `100%` }}>
        <Grid
          container
          direction="column"
          wrap="nowrap"
          gap="22px"
          xs={5}
          style={{
            padding: `24px`,
          }}
        >
          <Grid item container direction="column" wrap="nowrap" gap="6px">
            <Text variant="label" intlId="planner.plans.create_plan.label" />
            <InputText fieldValue={label} onChange={(value: any) => setLabel(value)} />
          </Grid>
          <Grid item container direction="column" wrap="nowrap" gap="6px">
            <Text variant="label" intlId="planner.plans.create_plan.status" />
            <RoutalSelect
              id="select-status"
              popoverProps={{
                options: [`draft`, `planning`].map(
                  (status): RoutalSelectMultiSelectOption => ({
                    key: status,
                    onClick: () => {
                      setUpdateStatus(status as PlanStatus);
                    },
                    content: (
                      <Grid container direction="row" wrap="nowrap" alignItems="center" style={{ overflow: `hidden` }}>
                        <div
                          style={{
                            maxWidth: `8px`,
                            maxHeight: `8px`,
                            minWidth: `8px`,
                            minHeight: `8px`,
                            borderRadius: `100%`,
                            backgroundColor: getPlanStatusColor(status as PlanStatus, `dark`),
                          }}
                        />

                        <HSpacer small />

                        <Text
                          tagName="span"
                          style={{
                            textAlign: `justify`,
                            whiteSpace: `nowrap`,
                            overflow: `hidden`,
                            width: `100%`,
                            textOverflow: `ellipsis`,
                            cursor: `pointer`,
                            color: updateStatus === status ? RoutalPalette.primary40 : undefined,
                          }}
                          intlId={`update_plan_status_dialog.status.${status}`}
                        />

                        {updateStatus === status && <CheckmarkIcon style={{}} />}
                      </Grid>
                    ),
                  })
                ),
              }}
            >
              <div
                style={{
                  width: `8px`,
                  height: `8px`,
                  minWidth: `8px`,
                  minHeight: `8px`,
                  borderRadius: `100%`,
                  backgroundColor: getPlanStatusColor(updateStatus as PlanStatus, `dark`),
                }}
              />

              <HSpacer small />

              <Text
                tagName="span"
                style={{
                  textAlign: `justify`,
                  whiteSpace: `nowrap`,
                  overflow: `hidden`,
                  width: `100%`,
                  textOverflow: `ellipsis`,
                  cursor: `pointer`,
                }}
                intlId={
                  updateStatus
                    ? `update_plan_status_dialog.status.${updateStatus}`
                    : `update_plan_status_dialog.select_status`
                }
              />
            </RoutalSelect>
          </Grid>
          <Grid item container direction="column" wrap="nowrap" gap="6px">
            <Text variant="label" intlId="planner.plans.create_plan.execution_date" />
            <RoutalInputDatePicker
              dateValue={executionDate}
              onChange={(date: Date) => {
                updateDate(toDayNoon(date));
              }}
              locale={user?.language.value}
              portalId="create-plan-dialog"
            />
          </Grid>
        </Grid>

        <Grid
          container
          direction="column"
          xs={7}
          style={{
            width: `100%`,
            padding: `24px`,
            border: `1px solid ${RoutalPalette.neutral10}`,
            borderBottom: `unset`,
            borderTop: `unset`,
            borderRight: `unset`,
            pointerEvents: isCreating ? `none` : undefined,
          }}
        >
          <VehiclesSelection
            allVehicles={allVehicles}
            paginatedVehicles={paginatedVehicles}
            isLoading={searchingVehicles}
            setOffset={setVehicleOffset}
            setFilterText={setFilterText}
            setSelectedVehicles={(vehiclesObject) => setAllVehicles(vehiclesObject ?? [])}
            executionDate={executionDate}
          />
        </Grid>
      </Grid>
    </RoutalDialog>
  );
};
